import { cx } from '../cx';

/**
 * A LEVEL BAR (redesign register L8, D9, R-K1; HIG Gauges, hig:
 * https://developer.apple.com/design/human-interface-guidelines/gauges; brief FE-FOUNDATION
 * section 9; components page section 4).
 *
 * The macOS kit's determinate linear progress indicator in shape (`Progress Indicators/<Light|
 * Dark>/Content Area/Linear/Determinate/2 Sm`: a pill track of a 6 percent wash with a hairline
 * edge), with L8's colours instead of the accent:
 *  - continuous (the program week, billing): green by default; past its end (past week 16) it
 *    is clamped full and orange; overdue money is the paid part green plus the overdue part red
 *    (R-K1), the rest the neutral track. Never blue, never a grey fill.
 *  - discrete (one segment per onboarding step): done green, late orange, open on the neutral
 *    track. A waived step draws NO segment (open question Q-FND-4, decided: as the existing
 *    `ProgressDots` and ledger R2-C-3), because skipped is not done (D9).
 *
 * ALWAYS WITH ITS WORDS BESIDE IT, which the caller prints ("Week 12 of 16", "4 of 9 done"),
 * so the bar itself is `aria-hidden`. Loading: a skeleton of the same size. Empty (no start
 * date, no amount): the caller prints its words and passes no value, and no track is drawn.
 */
export type LevelBarWidth = 'cell' | 'facts' | 'fill';
export type LevelSegment = 'done' | 'late' | 'open' | 'waived';

const WIDTHS: Readonly<Record<LevelBarWidth, string>> = {
  cell: 'w-(--level-bar-cell-width)',
  facts: 'w-(--level-bar-facts-width)',
  fill: 'w-full',
};

const TRACK = 'overflow-hidden rounded-pill bg-(--color-level-track) shadow-(--shadow-level-track)';

/** `ui/Skeleton`'s paint, at the bar's own size (its shapes carry heights of their own). */
const SKELETON = 'block animate-pulse rounded-pill bg-surface-sunken motion-reduce:animate-none';

const SEGMENT_FILL: Readonly<Record<Exclude<LevelSegment, 'waived'>, string>> = {
  done: 'bg-(--color-status-done)',
  late: 'bg-(--color-status-late)',
  open: 'bg-(--color-level-track) shadow-(--shadow-level-track)',
};

export interface ContinuousLevelProps {
  readonly kind: 'continuous';
  /** The filled amount (the week, the paid sum). Null draws nothing: the empty state. */
  readonly value: number | null;
  readonly max: number;
  /** The overdue amount after the paid part, drawn red (R-K1). */
  readonly overdue?: number | undefined;
  readonly width?: LevelBarWidth | undefined;
  /** `small` is the kit's 2 Sm (6), `large` its 3 Rg (10) for the billing bar. */
  readonly size?: 'small' | 'large' | undefined;
  readonly loading?: boolean | undefined;
  readonly className?: string | undefined;
}

export interface DiscreteLevelProps {
  readonly kind: 'discrete';
  readonly segments: readonly LevelSegment[];
  /** `card` 8 wide (a board card), `legend` 14 wide (components page section 4). */
  readonly segmentSize?: 'card' | 'legend' | undefined;
  readonly loading?: boolean | undefined;
  readonly className?: string | undefined;
}

export type LevelIndicatorProps = ContinuousLevelProps | DiscreteLevelProps;

export interface LevelParts {
  /** Percent of the track, 0 to 100. */
  readonly filled: number;
  readonly overdue: number;
  readonly tone: 'done' | 'late';
}

/**
 * THE ARITHMETIC, PURE: how much of the track is green or orange, and how much red.
 * Past the end the bar is clamped full and turns orange (L8 "orange past week 16"); the red
 * part never runs past the end of the track.
 */
export function levelParts(value: number, max: number, overdue = 0): LevelParts {
  if (!(max > 0)) return { filled: 0, overdue: 0, tone: 'done' };
  if (value > max) return { filled: 100, overdue: 0, tone: 'late' };
  const filled = Math.max(0, (value / max) * 100);
  const red = Math.max(0, Math.min(100 - filled, (overdue / max) * 100));
  return { filled, overdue: red, tone: 'done' };
}

/** The segments a waived step leaves out (Q-FND-4). */
export function drawnSegments(
  segments: readonly LevelSegment[],
): readonly Exclude<LevelSegment, 'waived'>[] {
  return segments.filter(
    (segment): segment is Exclude<LevelSegment, 'waived'> => segment !== 'waived',
  );
}

function percent(value: number): string {
  return `${String(Math.round(value * 100) / 100)}%`;
}

export function LevelIndicator(props: LevelIndicatorProps) {
  if (props.kind === 'discrete') {
    const width =
      props.segmentSize === 'card'
        ? 'w-(--step-segment-card-width)'
        : 'w-(--step-segment-legend-width)';
    const drawn = drawnSegments(props.segments);
    if (props.loading === true) {
      return (
        <span
          aria-hidden="true"
          className={cx(SKELETON, 'h-(--level-bar-height) w-24', props.className)}
        />
      );
    }
    return (
      <span
        aria-hidden="true"
        data-level="discrete"
        className={cx('inline-flex items-center gap-0.5', props.className)}
      >
        {drawn.map((segment, index) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: the segments are positional, one per step in a fixed order.
            key={index}
            data-segment={segment}
            className={cx(
              'block h-(--level-bar-height) rounded-pill',
              width,
              SEGMENT_FILL[segment],
            )}
          />
        ))}
      </span>
    );
  }

  const width = WIDTHS[props.width ?? 'fill'];
  const height = props.size === 'large' ? 'h-(--level-bar-height-large)' : 'h-(--level-bar-height)';
  if (props.loading === true) {
    return <span aria-hidden="true" className={cx(SKELETON, height, width, props.className)} />;
  }
  if (props.value === null) return null;
  const parts = levelParts(props.value, props.max, props.overdue ?? 0);
  return (
    <span
      aria-hidden="true"
      data-level="continuous"
      data-tone={parts.tone}
      className={cx('flex', TRACK, height, width, props.className)}
    >
      {parts.filled > 0 && (
        <span
          data-part="filled"
          className={cx(
            'block h-full',
            parts.overdue > 0 ? 'rounded-l-pill' : 'rounded-pill',
            parts.tone === 'late' ? 'bg-(--color-status-late)' : 'bg-(--color-status-done)',
          )}
          style={{ width: percent(parts.filled) }}
        />
      )}
      {parts.overdue > 0 && (
        <span
          data-part="overdue"
          className={cx(
            'block h-full bg-(--color-status-act-now)',
            parts.filled > 0 ? 'rounded-r-pill' : 'rounded-pill',
          )}
          style={{ width: percent(parts.overdue) }}
        />
      )}
    </span>
  );
}
